/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import AssertionError from 'assertion-error';
import { DateNowFactory, wholeLocalHour } from '../date/date-util';
import {
  assertIsDateISOStringRange,
  maxISOStringTime,
  minISOStringTime,
  toISOString
} from '../iso-string';
import Numeric from '../numeric/numeric';

/**
 * @import { IAnalyticsTracker, IMedium, MutexContext } from '.'
 * @import { IDateUTC, NowFactory } from '../date'
 * @import { ISOString } from '../iso-string'
 * @import { Medium } from '../medium/medium'
 * @import { Content } from '../content/content'
 * @import { ServerDeviceId } from '../session/session-attrs'
 * @import { AnalyticsCounter } from './analytics-counter'
 * @import { AnalyticsCounterAttrs } from './analytics-counter-attrs'
 * @import { AnalyticsGroup } from './analytics-group'
 * @import { AnalyticsGroupAttrs } from './analytics-group-attrs'
 * @import AnalyticsStore from './analytics-store'
 */

/**
 * Správce sběru analytických dat.
 * @implements {IAnalyticsTracker}
 */
export default class AnalyticsTracker {
  /**
   * Vytvoří instanci.
   * @param {AnalyticsStore} analyticsStore Úložiště analytických dat s mutexem.
   * @param {Object} [opts = {}]
   * @param {DateConstructor} [opts.Date] Konstruktor datumů (používá se pro
   * {@link wholeLocalHour}).
   *
   * Nutné pro deterministickou testovatelnost, aby šlo zafixovat lokální TZ,
   * tj. {@link Date.prototype.getTimezoneOffset}.
   * @param {NowFactory} [opts.nowFactory] Továrna na aktuální čas.
   */
  constructor(
    analyticsStore,
    { Date = window.Date, nowFactory = new DateNowFactory() } = {}
  ) {
    /** @private */
    this.analyticsStore = analyticsStore;
    /** @private  Konstruktor pro převody UTC na lokální čas a zpět. */
    this.Date = Date;
    /** @private Továrna na aktuálni UTC čas. */
    this.nowFactory = nowFactory;

    /** Flag indikujúci zbieranie analytických dát. */
    this.enabled = false;
  }

  reset() {
    this.enabled = false;
  }

  /** @type {IAnalyticsTracker['trackAnalytics']} */
  async trackAnalytics(content) {
    // Pokud není povoleno zaznamenávání analytických dat, nebo nebylo přehráno
    // médium, data se nezaznamenávají a nezařazují do fronty. Test na hasMedium
    // je zde duplikovaný s mutexovým trackAnalytics, jelikož je to levná zkratka
    // pro zbytečné nezařazení do fronty.
    if (!this.enabled || !content.medium) {
      return;
    }

    return await this.analyticsStore.doInMutex(async (ctx) => {
      await trackAnalytics(ctx, content, this.nowFactory.now(), this.Date);
    });
  }
}

/**
 * Vrátí nebo vytvoří (pokud neexistuje žádná aktuální) analytickou skupinu
 * pro aktuální čas a časovou zónu (skrze {@link Date}).
 * @param {MutexContext} ctx
 * @param {IDateUTC} now
 * @param {DateConstructor} Date
 * @returns {Promise<AnalyticsGroup>}
 * @throws {RangeError} {@link toISOString}
 */
async function ensureGroup(ctx, now, Date) {
  const attrs = getGroupAttrs(Date, now, ctx.serverDeviceId);
  let group = await ctx.store.findAnalyticsGroup(attrs.id);
  if (!group) {
    group = await ctx.store.createAnalyticsGroup(attrs);
  }
  return group;
}

/**
 * Generování atributů počítadla.
 *
 * Generuje rovněž deterministického ID počítadla (nikdy zásadně neměnit).
 * @param {AnalyticsGroup['id']} analyticsGroupId ID analytické skupiny, do které počítadlo patří.
 * @param {IMedium} medium Přehrané médium, ke kterému se počítadlo vztahuje.
 * @param {number} played Počet přehrání.
 * @returns {AnalyticsCounterAttrs}
 */
export function getCounterAttrs(analyticsGroupId, medium, played) {
  return {
    analyticsGroupId,
    id: `${analyticsGroupId}/${medium.id}`,
    mediumId: medium.id,
    name: medium.name,
    played,
    type: medium.type
  };
}

/**
 * @param {DateConstructor} Date Pro kopii a práci s lokálním časem a TZ
 * (explicitně pro testovatelnost).
 * @param {IDateUTC} now
 * @param {ServerDeviceId} serverDeviceId
 * @returns {AnalyticsGroupAttrs}
 * @throws {RangeError} {@link toISOString}
 */
export function getGroupAttrs(Date, now, serverDeviceId) {
  const opened = assertIsDateISOStringRange(wholeLocalHour(Date, now, false));
  const closed = assertIsDateISOStringRange(wholeLocalHour(Date, now, true));
  return {
    id: getGroupId(opened, closed),
    opened: toISOString(opened),
    closed: toISOString(closed),
    serverUrl: serverDeviceId.serverUrl,
    deviceId: serverDeviceId.deviceId
  };
}

/**
 * Generování deterministického ID skupiny (nikdy zásadně neměnit).
 *
 * Hodnota musí zahrnovat opened, ale také kvůli variabilní délce kvůli DST
 * a TZ posunům i délku, aby interval byl vždy unikátní. Důležité zejména,
 * pokud se na zařízení změní TZ např. i z/na celohodinové offsety, jelikož
 * případná změna DST může vygenerovat 2h interval se stejným opened.
 *
 * Jako vedlejší efekt a bonus je ID lexikograficky seřaditelné podle opened
 * a duration.
 * @param {IDateUTC} opened Date v {@link ISOString} rozsahu.
 * @param {IDateUTC} closed Date v {@link ISOString} rozsahu.
 * @returns {string}
 */
function getGroupId(opened, closed) {
  // Posun opened tak, abychom pro validní rozsah měli unsigned number.
  let id = Numeric.toStringPadded(
    opened.getTime() - minISOStringTime,
    getGroupIdOpenedShiftedDigits,
    getGroupIdRadix
  );

  const durationMinusHour =
    closed.getTime() - opened.getTime() - 60 * 60 * 1000;
  // TODO #389: Assert, nemělo by nikdy nastat. Dodat důkaz.
  if (durationMinusHour < 0) {
    throw new AssertionError('duration is shorter than hour', {
      opened,
      closed,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }
  // Duration se kóduje podobným způsobem jako Google Ordered Code kódování
  // pro uint64 (první byte je počet následujících bytů big-endian reprezentace,
  // kde se nekódují leading zeros včetně samotné 0), akorát místo base256 pole
  // bytů enkóduje rovnou stringově
  // do base32hex pole znaků.
  // - https://github.com/google/orderedcode/blob/master/orderedcode.go#L335
  //
  // Pro durationMinuHour 0 se nekóduje ani délka ani hodnota, což ale stále
  // zaručuje lexikografické řazení podle opened a duration(MinusHour).
  if (durationMinusHour > 0) {
    const s = durationMinusHour.toString(getGroupIdRadix);
    id = id.concat(s.length.toString(getGroupIdRadix), s);
  }

  return id;
}

const getGroupIdRadix = 32;
const getGroupIdOpenedShiftedDigits = Math.ceil(
  Math.ceil(Math.log2(maxISOStringTime - minISOStringTime)) /
    Math.log2(getGroupIdRadix)
);

/**
 * Zaznamená analytická data pro médium.
 * @param {MutexContext} ctx
 * @param {Content} content
 * @param {IDateUTC} now
 * @param {DateConstructor} Date
 * @returns {Promise<AnalyticsCounter | undefined>}
 * @throws {RangeError} {@link toISOString}
 */
async function trackAnalytics(ctx, content, now, Date) {
  if (!content.medium) {
    return;
  }

  const analyticsGroup = await ensureGroup(ctx, now, Date);
  const { medium } = content;
  const attrs = getCounterAttrs(analyticsGroup.id, medium, 1);
  const counter = await ctx.store.findAnalyticsCounter(attrs.id);

  if (counter) {
    counter.played += 1;
    await ctx.store.saveAnalyticsCounter(counter.id);
    return counter;
  }

  return await ctx.store.createAnalyticsCounter(attrs);
}

export const internal = {
  getGroupId,
  trackAnalytics
};
